import { ReactNode, useState } from 'react';
import { Col, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import { FaHourglass } from 'react-icons/fa6';
import { BsCalendar, BsClock, BsFillGeoAltFill, BsPeople, BsX } from 'react-icons/bs';
import { MdOutlineRestaurant } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { Event } from '../SFCCalendar';
import './EventWrapper.css';
import { displaySessionTimeRange, formatDate, formatUTCDateOnly } from '../../../../utils/dateUtils';
import { hasPendingSubRequest } from '../../../../utils/lessonInstructorUtils';
import { getCalendarTraineeObserverNoteText } from './CalendarTraineeObserverNote';
interface EventWrapperProps {
  event: Event;
  children?: React.ReactNode;
  summarized?: boolean;
  filteredInstructorId?: number | null;
}

const PopoverRow = ({ icon, text }: { icon: ReactNode; text: string }) => (
  <Row className="text-secondary mb-1 mt-1">
    <Col
      xs={1}
      style={{ fontSize: 16 }}
    >
      {icon}
    </Col>
    <Col style={{ fontSize: 18 }}>{text}</Col>
  </Row>
);

const PopoverBody = ({
  event,
  onClose,
  filteredInstructorId
}: {
  event: Event;
  onClose?: () => void;
  filteredInstructorId?: number | null;
}) => {
  const pendingSub =
    filteredInstructorId &&
    event.lesson?.lesson_assignments &&
    hasPendingSubRequest(event.lesson.lesson_assignments, filteredInstructorId);

  const traineeObserverNote = getCalendarTraineeObserverNoteText(
    event.lesson,
    event.lesson?.course,
    filteredInstructorId
  );

  return (
  <Popover.Body className="event-popover-body">
    {onClose && (
      <button
        onClick={onClose}
        className="event-close-button"
      >
        <BsX />
      </button>
    )}
    <Link
      className="fw-bolder text-info text-uppercase event-title-link"
      to={`/admin/schedule/sessions/${event.lesson?.course_id}`}
    >
      {event.title}
    </Link>
    <PopoverRow
      icon={<BsFillGeoAltFill />}
      text={`${event.lesson?.course.venue.address}
        ${event.lesson?.course.venue.city}, ${event.lesson?.course.venue.state}
        ${event.lesson?.course.venue.zip}`}
    />
    <PopoverRow
      icon={<BsCalendar />}
      text={
        event.lesson?.date
          ? formatUTCDateOnly(new Date(String(event.lesson.date).slice(0, 10)), 'MMMM d, yyyy')
          : formatDate(event.start!, 'MMMM d, yyyy')
      }
    />
      <PopoverRow
          icon={<BsClock />}
          text={
              displaySessionTimeRange(
                  event.lesson?.calculated_starts_at ?? event.lesson?.course?.starts_at,
                  event.lesson?.calculated_ends_at ?? event.lesson?.course?.ends_at,
                  (event.lesson?.course?.venue as any)?.location?.timezone,
              ) || event.lesson?.formatted_time || ''
          }
      />
    <PopoverRow
      icon={<MdOutlineRestaurant />}
      text={
        event.lesson?.lesson_plans && event.lesson?.lesson_plans.length > 0
          ? event.lesson?.lesson_plans.map((plan) => plan.name).join(' + ')
          : 'No lesson plans'
      }
    />
    {pendingSub && <PopoverRow icon={<FaHourglass />} text="Sub requested" />}
    {traineeObserverNote && <PopoverRow icon={<BsPeople />} text={traineeObserverNote} />}
  </Popover.Body>
  );
};

export const EventWrapper: React.FC<EventWrapperProps> = ({
  event,
  children,
  summarized,
  filteredInstructorId
}) => {
  const [show, setShow] = useState(false);

  const handleClose = () => {
    setShow(false);
  };

  const handleToggle = () => {
    setShow(!show);
  };

  return !summarized ? (
    <OverlayTrigger
      show={show}
      onToggle={handleToggle}
      trigger="click"
      placement="auto"
      overlay={
        <Popover id="event-popover">
          <PopoverBody
            event={event}
            onClose={handleClose}
            filteredInstructorId={filteredInstructorId}
          />
        </Popover>
      }
      rootClose
    >
      <div>{children}</div>
    </OverlayTrigger>
  ) : (
    <></>
  );
};
